import React, { useState, useEffect } from 'react';
import { Save, Volume2, Settings, MessageCircle, Plus, Trash2, ToggleLeft, ToggleRight, RefreshCw } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const VoiceAgentAdmin = () => {
  const [settings, setSettings] = useState({
    is_enabled: true,
    show_widget: true,
    show_page: true,
    agent_name: 'Priya',
    agent_photo: '/priya-avatar.png',
    welcome_message: 'नमस्ते! मैं प्रिया हूं, आपकी सहायक। मैं ANANT WEB PORTAL के बारे में आपके सभी सवालों का जवाब दे सकती हूं।',
    system_prompt: '',
    voice_lang: 'hi-IN',
    voice_rate: 1.0,
    voice_pitch: 1.0,
    whatsapp_number: '919579369830',
    custom_commands: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [newCommand, setNewCommand] = useState({ trigger: '', response: '' });
  const [activeTab, setActiveTab] = useState('settings');

  useEffect(() => {
    fetchSettings();
    fetchConversations();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/voice-agent/settings');
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    try {
      const res = await api.get('/voice-agent/conversations');
      setConversations(res.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/voice-agent/settings', settings);
      toast.success('Voice agent settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addCustomCommand = async () => {
    if (!newCommand.trigger || !newCommand.response) {
      toast.error('Please fill both trigger and response');
      return;
    }
    
    try {
      await api.post('/voice-agent/commands', {
        trigger: newCommand.trigger,
        response: newCommand.response,
        is_active: true
      });
      toast.success('Custom command added!');
      setNewCommand({ trigger: '', response: '' });
      fetchSettings();
    } catch (error) {
      toast.error('Failed to add command');
    }
  };

  const deleteCommand = async (commandId) => {
    try {
      await api.delete(`/voice-agent/commands/${commandId}`);
      toast.success('Command deleted');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to delete command');
    }
  };

  const deleteConversation = async (sessionId) => {
    try {
      await api.delete(`/voice-agent/conversations/${sessionId}`);
      toast.success('Conversation deleted');
      fetchConversations();
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  const testVoice = () => {
    if (!window.speechSynthesis) {
      toast.error('Speech synthesis not supported in this browser');
      return;
    }
    
    const utterance = new SpeechSynthesisUtterance('नमस्ते, मैं प्रिया हूं। Hello, I am Priya.');
    utterance.lang = settings.voice_lang;
    utterance.rate = settings.voice_rate;
    utterance.pitch = settings.voice_pitch;
    
    window.speechSynthesis.speak(utterance);
    toast.success('Playing test voice...');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="voice-agent-admin">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <MessageCircle className="w-8 h-8 text-primary" />
              Voice Agent Settings
            </h1>
            <p className="text-muted-foreground">Configure Priya - Your AI Assistant</p>
          </div>
          <button
            onClick={saveSettings}
            disabled={saving}
            className="btn-primary flex items-center gap-2"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['settings', 'commands', 'conversations'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                activeTab === tab ? 'bg-primary text-primary-foreground' : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Basic Settings */}
            <div className="glass-card p-6 space-y-6">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary" />
                Basic Settings
              </h2>

              {/* Toggle Controls */}
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <p className="font-medium">Enable Voice Agent</p>
                    <p className="text-sm text-muted-foreground">Turn Priya on/off globally</p>
                  </div>
                  <button
                    onClick={() => setSettings({ ...settings, is_enabled: !settings.is_enabled })}
                    className={`p-2 rounded-lg ${settings.is_enabled ? 'text-green-500' : 'text-muted-foreground'}`}
                  >
                    {settings.is_enabled ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <p className="font-medium">Show Floating Widget</p>
                    <p className="text-sm text-muted-foreground">Chat bubble on all pages</p>
                  </div>
                  <button
                    onClick={() => setSettings({ ...settings, show_widget: !settings.show_widget })}
                    className={`p-2 rounded-lg ${settings.show_widget ? 'text-green-500' : 'text-muted-foreground'}`}
                  >
                    {settings.show_widget ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <p className="font-medium">Show Dedicated Page</p>
                    <p className="text-sm text-muted-foreground">/talk-to-priya page</p>
                  </div>
                  <button
                    onClick={() => setSettings({ ...settings, show_page: !settings.show_page })}
                    className={`p-2 rounded-lg ${settings.show_page ? 'text-green-500' : 'text-muted-foreground'}`}
                  >
                    {settings.show_page ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
                  </button>
                </div>
              </div>

              {/* Agent Details */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Agent Name</label>
                  <input
                    type="text"
                    value={settings.agent_name}
                    onChange={(e) => setSettings({ ...settings, agent_name: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">WhatsApp Number (with country code)</label>
                  <input
                    type="text"
                    value={settings.whatsapp_number}
                    onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="919579369830"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Welcome Message</label>
                  <textarea
                    value={settings.welcome_message}
                    onChange={(e) => setSettings({ ...settings, welcome_message: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Voice Settings */}
            <div className="glass-card p-6 space-y-6">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-primary" />
                Voice Settings
              </h2>

              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
                <img 
                  src={settings.agent_photo || '/priya-avatar.png'} 
                  alt="Priya"
                  className="w-20 h-20 rounded-full object-cover border-2 border-primary"
                />
                <div>
                  <p className="font-medium">{settings.agent_name}</p>
                  <p className="text-sm text-muted-foreground">AI Assistant</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Voice Language</label>
                <select
                  value={settings.voice_lang}
                  onChange={(e) => setSettings({ ...settings, voice_lang: e.target.value })}
                  className="w-full px-4 py-2 bg-background border border-white/10 rounded-lg"
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="hi-IN">Hindi (India)</option>
                  <option value="en-IN">English (India)</option>
                  <option value="en-US">English (US)</option>
                  <option value="en-GB">English (UK)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Voice Speed: {settings.voice_rate}x</label>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={settings.voice_rate}
                  onChange={(e) => setSettings({ ...settings, voice_rate: parseFloat(e.target.value) })}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Voice Pitch: {settings.voice_pitch}</label>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={settings.voice_pitch}
                  onChange={(e) => setSettings({ ...settings, voice_pitch: parseFloat(e.target.value) })}
                  className="w-full"
                />
              </div>

              <button
                onClick={testVoice}
                className="w-full px-4 py-3 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 flex items-center justify-center gap-2"
              >
                <Volume2 className="w-5 h-5" />
                Test Voice
              </button>

              <div>
                <label className="block text-sm font-medium mb-2">Custom System Prompt (AI Personality)</label>
                <textarea
                  value={settings.system_prompt}
                  onChange={(e) => setSettings({ ...settings, system_prompt: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  rows={6}
                  placeholder="Leave empty for default Priya personality..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Custom Commands Tab */}
        {activeTab === 'commands' && (
          <div className="glass-card p-6">
            <h2 className="text-xl font-semibold mb-6">Custom Commands</h2>
            <p className="text-muted-foreground mb-6">
              Add custom trigger words/phrases and automatic responses. These will be matched before AI processing.
            </p>

            {/* Add New Command */}
            <div className="grid md:grid-cols-2 gap-4 mb-6 p-4 bg-white/5 rounded-lg">
              <div>
                <label className="block text-sm font-medium mb-2">Trigger Word/Phrase</label>
                <input
                  type="text"
                  value={newCommand.trigger}
                  onChange={(e) => setNewCommand({ ...newCommand, trigger: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  placeholder="e.g., hello, pricing, contact"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Response</label>
                <input
                  type="text"
                  value={newCommand.response}
                  onChange={(e) => setNewCommand({ ...newCommand, response: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  placeholder="Automatic response text"
                />
              </div>
              <div className="md:col-span-2">
                <button
                  onClick={addCustomCommand}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Command
                </button>
              </div>
            </div>

            {/* Existing Commands */}
            <div className="space-y-3">
              {settings.custom_commands?.map((cmd, idx) => (
                <div key={cmd.id || idx} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium">Trigger: <span className="text-primary">"{cmd.trigger}"</span></p>
                    <p className="text-sm text-muted-foreground">Response: {cmd.response}</p>
                  </div>
                  <button
                    onClick={() => deleteCommand(cmd.id)}
                    className="p-2 text-red-500 hover:bg-red-500/20 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {(!settings.custom_commands || settings.custom_commands.length === 0) && (
                <p className="text-center text-muted-foreground py-8">No custom commands yet</p>
              )}
            </div>
          </div>
        )}

        {/* Conversations Tab */}
        {activeTab === 'conversations' && (
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Recent Conversations</h2>
              <button
                onClick={fetchConversations}
                className="p-2 bg-white/5 rounded-lg hover:bg-white/10"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {conversations.map((conv) => (
                <div key={conv.session_id} className="p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-muted-foreground">
                      Session: {conv.session_id?.slice(0, 20)}...
                    </p>
                    <button
                      onClick={() => deleteConversation(conv.session_id)}
                      className="p-1 text-red-500 hover:bg-red-500/20 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {conv.messages?.slice(-4).map((msg, idx) => (
                      <div key={idx} className={`text-sm ${msg.role === 'user' ? 'text-primary' : 'text-muted-foreground'}`}>
                        <span className="font-medium">{msg.role === 'user' ? 'User' : 'Priya'}:</span> {msg.content.slice(0, 100)}...
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {conversations.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No conversations yet</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceAgentAdmin;
